import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { ErrorType } from "@/types";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { displayValue } from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import TableFilters from "@/components/ui/table/data-table-filter";
import { PlusIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import HardwareIntegrationForm from "../modal";
import { useGetAllHardwareQuery } from "@/services/hardwareApi";
import { HardwareIntegrationRow } from "@/types/system-setting/hardware-integration";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { useGetFacilitiesQuery } from "@/services/facilityApis";

interface SearchCriteria {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "hardware",
};

export default function HardwareIntegrationTable() {
  const hard_int = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).hard_int ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { data: facilitiesData } = useGetFacilitiesQuery({
    orgId: orgId,
    query: "",
  });

  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData(initialValue);

  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const [action, setAction] = useState<"add" | "edit">("add");
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [data, setData] = useState<HardwareIntegrationRow | undefined>(
    undefined
  );

  function handleRoute() {
    setAction("add");
    setIsDialogOpen(true);
  }

  const {
    data: hardwareData,
    isLoading: ishardwaredataloading,
    refetch,
    error,
    isError,
  } = useGetAllHardwareQuery(
    {
      org_id: orgId,
      query: query,
    },
    {
      skip: query == "",
    }
  );

  React.useEffect(() => {
    if (isError && navigator.onLine) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: typedError.data?.detail ?? typedError.data?.message,
      });
    }
  }, [isError]);

  const hardwareTableData = React.useMemo(() => {
    return Array.isArray(hardwareData?.data) ? hardwareData?.data : [];
  }, [hardwareData]);

  const { toast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const handleEdit = (data: HardwareIntegrationRow) => {
    setAction("edit");
    setData(data);
    setIsDialogOpen(true);
  };
  const actionsColumn: ColumnDef<HardwareIntegrationRow> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={hard_int}
        row={row.original.id}
        data={row?.original}
        refetch={refetch}
        handleEdithadrwareForm={handleEdit}
      />
    ),
  };

  const columns: ColumnDef<HardwareIntegrationRow>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      meta: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="capitalize cursor-pointer">
                        <span>
                          {displayValue(
                            `${row.original.name}`.length > 15
                              ? `${row.original.name}`.substring(0, 15) + "..."
                              : `${row.original.name}`
                          )}
                        </span>
                      </p>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="capitalize text-sm">
                        {displayValue(`${row?.original?.name}`)}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "description",
      meta: "Description",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Description"
          sortKey="description"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.description}`.length > 15
                          ? `${row.original.description}`.substring(0, 15) +
                          "..."
                          : `${row.original.description}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="capitalize text-sm">
                    {displayValue(`${row?.original?.description}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
    {
      accessorKey: "facility_name",
      meta: "Facility Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Facility Name"
          sortKey="facility_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>
                  {displayValue(row.original.facility_name?.toString())}
                </span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "connection_key",
      meta: "Connection Key",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Connection Key"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="cursor-pointer normal-case">
                    <span>
                      {displayValue(
                        `${row.original.connection_key}`.length > 15
                          ? `${row.original.connection_key}`.substring(0, 15) +
                          "..."
                          : `${row.original.connection_key}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="capitalize text-sm">
                    {displayValue(`${row?.original?.connection_key}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },

    ...(hard_int !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: hardwareTableData as HardwareIntegrationRow[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const totalRecords = hardwareData?.filtered_counts || 0;

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteria>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });
  function handleFilterChange(field: string, value: string | number) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }
  const filterDisplay = [
    {
      type: "multiselect",
      name: "facility_id",
      label: "Facility",
      options: facilitiesData?.data.map((facility) => ({
        label: facility.name,
        value: facility.id,
      })),
      function: (value: string) => handleFilterChange("facility_id", value),
    },
  ];
  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        {/* Table Filter component */}
        <div className="w-full lg:flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={setSearchCriteria}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>

        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {hard_int !== "read" && (
            <Button
              className="bg-primary text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
              onClick={handleRoute}
            >
              <PlusIcon className="size-4" />
              Create New
            </Button>
          )}

          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      <div className="hidden md:block rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {ishardwaredataloading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <>
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      onClick={(e) => {
                        if (hard_int !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                          handleEdit(row.original)
                        }
                      }}
                      className={hard_int != "read" ? "cursor-pointer hover:bg-secondary" : ""}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="h-8">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </>
                ))
              ) : hardwareData?.total_counts == 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No Hardware Integration added yet.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found for the search Criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3">
        {ishardwaredataloading ? (
          <div className="flex space-x-2 justify-center items-center py-10">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : hardwareTableData.length ? (
          hardwareTableData.map((row: HardwareIntegrationRow) => (
            <div
              key={row.id}
              className="border border-border rounded-md p-3 space-y-2 bg-white"
              onClick={() => {
                if (hard_int !== "read") {
                  handleEdit(row);
                }
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-sm capitalize truncate">
                    {displayValue(row.name)}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {displayValue(row.facility_name?.toString())}
                  </p>
                </div>
                <div
                  className="shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {hard_int !== "read" && (
                    <DataTableRowActions
                      access={hard_int}
                      row={row.id}
                      data={row}
                      refetch={refetch}
                      handleEdithadrwareForm={handleEdit}
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <div className="text-gray-500">Description</div>
                <div className="text-right truncate normal-case">{displayValue(row.description)}</div>

                <div className="text-gray-500">Connection Key</div>
                <div className="text-right truncate normal-case">{displayValue(row.connection_key)}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            No records found.
          </div>
        )}
      </div>

      {/* pagination */}
      {hardwareTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
      <HardwareIntegrationForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        editModeData={data as HardwareIntegrationRow}
        refetch={refetch}
      />
    </div>
  );
}
